import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Phone,
  Fingerprint,
  Lock
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import '../assets/css/style.css';
import bg from '../assets/images/bg.jpeg';
import { useDispatch, useSelector } from 'react-redux';
import { setIsLoggedIn, setUser } from '../store/authSlice';
import axios from 'axios';
import API_BASE_URL from '../config';

// Styled Components
const LoginContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
}));

const LoginPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: 450,
  width: '100%',
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
}));

const Title = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(4),
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontWeight: 'bold',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius * 2,
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const LoginButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius * 2,
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  '&:hover': {
    background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
  },
}));

const OTPTimer = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  marginTop: theme.spacing(2),
  color: theme.palette.text.secondary,
}));

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState('');
  const [message, setMessage] = useState('');
  const [timer, setTimer] = useState(90);
  const [isOTPSent, setIsOTPSent] = useState(false);
  const [isUidLogin, setIsUidLogin] = useState(false);
  const [email_id, setEmail] = useState('');
  const [phone_number, setPhone] = useState('');
  const [uid, setUid] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checkingStatus, setCheckingStatus] = useState(false);

  // Add input type detection
  const isPhoneNumber = (value) => /^\d{10}$/.test(value);
  const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const validateInput = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\d{10}$/;
    const uidRegex = /^[A-Z0-9]{8,}$/;

    return emailRegex.test(value) || phoneRegex.test(value) || uidRegex.test(value);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    // If it's a phone number, only allow digits and limit to 10 characters
    if (/^\d*$/.test(value) && value.length <= 10) {
      setInput(value);
    } else if (!/^\d*$/.test(value)) {
      // If it's not a phone number, allow any input
      setInput(value);
    }
  };

  const fetchAndSetOTP = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/get-otp`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ input: input }),
      });
      console.log('response', response);
      if (!response.ok) {
        throw new Error('Failed to fetch OTP');
      }

      const data = await response.json();
      setOtp(data.otp);
      return data.otp;
    } catch (error) {
      console.error('Error fetching OTP:', error);
      setError('Error fetching OTP');
      return null;
    }
  };

  const verifyOTP = async (otp) => {
    try {
      const isPhone = /^\d{10}$/.test(input);
      const requestBody = isPhone
        ? { phone_number: input, otp }
        : { email_id: input, otp };

      const response = await fetch(`${API_BASE_URL}/verify-otp`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to verify OTP');
      }

      const data = await response.json();

      // Check if user is disabled
      if (data.status == 1) {
        setError('Your account is disabled. Please contact administrator.');
        return;
      }

      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('uid', data.uid);
      localStorage.setItem('userRole', String(data.role));
      window.dispatchEvent(new Event('planUpdated'));

      // Redirect based on role
      if (String(data.role) === '1') {
        navigate('/admin/dashboard');
      } else {
        navigate('/home');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      setError('Error verifying OTP');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!validateInput(input)) {
      setError('Please enter a valid email, phone number, or UID');
      setLoading(false);
      return;
    }

    try {
      if (isUidLogin) {
        const response = await fetch(`${API_BASE_URL}/uid-login`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ uid: input, password }),
        });

        const data = await response.json();
        if (response.ok) {
          // Check if user is disabled
if (data.status == 1) {
            setError('Your account is disabled. Please contact administrator.');
            return;
          }
          console.log('data', data);
          localStorage.setItem('isLoggedIn', 'true');
          localStorage.setItem('uid', data.uid);
          localStorage.setItem('userRole', String(data.role));
          window.dispatchEvent(new Event('planUpdated'));

          // Redirect based on role
          if (String(data.role) === '1') {
            navigate('/admin/dashboard');
          } else {
            navigate('/home');
          }
        } else {
          setError(data.message || 'Login failed');
        }
      } else {
        if (!isOTPSent) {
          const response = await fetch(`${API_BASE_URL}/send-otp`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ input }),
          });

          const data = await response.json();
          if (response.ok) {
            setIsOTPSent(true);
            setTimer(90);
            const fetchedOTP = await fetchAndSetOTP();
            console.log('fetchedOTP', fetchedOTP);
            if (fetchedOTP) {
              // Wait for 1 second to show the OTP in the field
              setTimeout(() => {
                //  verifyOTP(fetchedOTP);
              }, 1000);
            }
          } else {
            setError(data.message || 'Failed to send OTP');
          }
        } else {
          //  await verifyOTP(otp);
        }
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };





  return (
    <LoginContainer>
      <LoginPaper elevation={3}>
        <Title variant="h4">Welcome to Gold Petals</Title>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleLogin}>
          <StyledTextField
            fullWidth
            label={isUidLogin ? "Unique ID" : "Email / Phone Number"}
            variant="outlined"
            value={input}
            onChange={handleInputChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {isUidLogin ? (
                    <Fingerprint />
                  ) : isPhoneNumber(input) ? (
                    <Phone />
                  ) : (
                    <Email />
                  )}
                </InputAdornment>
              ),
            }}
            helperText={isPhoneNumber(input) ? "Enter 10-digit mobile number" : ""}
          />

          {isUidLogin ? (
            <StyledTextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          ) : isOTPSent && (
            <StyledTextField
              fullWidth
              label="OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
              }}
            />
          )}

          {isOTPSent && (
            <OTPTimer variant="body2">
              {timer > 0 ? `Resend OTP in ${timer}s` : 'OTP expired'}
            </OTPTimer>
          )}

          <LoginButton
            id="verify-otp"
            fullWidth
            variant="contained"
            type="submit"
            disabled={loading || checkingStatus}
            onClick={async () => {
              if (isOTPSent) {
                await verifyOTP(otp);
              }
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : checkingStatus ? (
              'Checking Account Status...'
            ) : isUidLogin ? (
              'Login'
            ) : isOTPSent ? (
              'Verify OTP'
            ) : (
              'Send OTP'
            )}
          </LoginButton>

          <Box mt={2} textAlign="center">
            <Button
              color="primary"
              onClick={() => setIsUidLogin(!isUidLogin)}
            >
              {isUidLogin ? 'Login with OTP' : 'Login with UID'}
            </Button>
            <Button
              color="primary"
              onClick={() => navigate('/guest')} >
              Guest Login
            </Button>
          </Box>

          <Box
            mt={3}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Don't have an account?
            </Typography>
            <Button
              color="primary"
              onClick={() => navigate('/signup')}
              sx={{ textDecoration: 'underline', textTransform: 'none' }}
            >
              Create Account
            </Button>
          </Box>
        </form>
      </LoginPaper>
    </LoginContainer>
  );
};

export default Login;
